import { Spacer } from '@eatclubapp/ec-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { updateBookingAction } from '../../../../../data/actions/bookingAction';
import { bookingKeys, bookingPropTypes } from '../../../../../data/models/Booking';
import { experiencesPropTypes } from '../../../../../data/models/Experience';
import {
  findObjectByProperty,
  isEmpty,
  updateObjectByProperty,
} from '../../../../../utils/helpers';
import ExperienceCard from './ExperienceCard';
import useStyles from './ExperienceListStyles';

const ExperienceList = ({ updateBooking, experiences, booking }) => {
  const classes = useStyles();
  const hasImages = experiences.data?.some((experience) => !isEmpty(experience?.imageUrl));

  const toggleExperience = (newExperience) => {
    const count = booking.guests;
    const experienceWithCount = {
      id: newExperience?.id,
      count,
      experience: newExperience,
    };

    const selectedExperienceIds = booking?.selectedExperiences.map((experience) => experience?.id);

    // Toggle it on or off by adding it to the list
    if (selectedExperienceIds?.includes(newExperience?.id)) {
      updateBooking(
        bookingKeys.selectedExperiences,
        booking?.selectedExperiences.filter((experience) => experience?.id !== newExperience.id),
      );
      return;
    }

    // Add it to the list.
    // soldAlone can't have other experiences selected
    if (newExperience?.soldAlone) {
      updateBooking(bookingKeys.selectedExperiences, [experienceWithCount]);
      return;
    }

    // Otherwise add it to the list
    updateBooking(bookingKeys.selectedExperiences, [
      ...booking?.selectedExperiences,
      experienceWithCount,
    ]);
  };

  /**
   * If an experience was selected, add it to the list
   * If already in the list, increment its count
   * @param newExperience
   * @param newCount
   */
  const handleSelectExperience = (newExperience, newCount = 1) => {
    const experienceAlreadySelected = booking?.selectedExperiences?.some(
      (experience) => experience?.id === newExperience?.id,
    );

    // Increment the count if already in the list
    if (experienceAlreadySelected) {
      let updatedExperience = findObjectByProperty(
        booking?.selectedExperiences,
        newExperience.id,
        'id',
      );

      updatedExperience = {
        ...updatedExperience,
        count: newCount,
      };

      updateBooking(
        bookingKeys.selectedExperiences,
        updateObjectByProperty(booking?.selectedExperiences, updatedExperience, 'id'),
      );
      return;
    }

    // Otherwise add it to the list
    const experienceWithCount = {
      id: newExperience?.id,
      count: newCount,
      experience: newExperience,
    };

    // Add it to the list.
    // soldAlone can't have other experiences selected
    if (newExperience?.soldAlone) {
      updateBooking(bookingKeys.selectedExperiences, [experienceWithCount]);
      return;
    }

    // Otherwise add it with the others
    updateBooking(bookingKeys.selectedExperiences, [
      ...booking?.selectedExperiences,
      experienceWithCount,
    ]);
  };

  const handleDeselectExperience = (newExperience) => {
    updateBooking(
      bookingKeys.selectedExperiences,
      booking?.selectedExperiences.filter((experience) => experience?.id !== newExperience.id),
    );
  };

  return (
    <Spacer direction='vertical' className={classes.container} gap={12}>
      {experiences?.data?.map((experience) => (
        <Spacer
          gap={16}
          direction='vertical'
          key={`${booking?.date}-${experience.id}-${experience?.price}`}
        >
          <ExperienceCard
            experience={experience}
            onClick={() => toggleExperience(experience)}
            onSelect={(count) => handleSelectExperience(experience, count)}
            onDeselect={() => handleDeselectExperience(experience)}
            isSelected={booking?.selectedExperiences
              .filter((experienceCount) => experienceCount.count > 0)
              .some((selectedExperience) => selectedExperience.id === experience.id)}
            guestAmount={booking?.guests}
            count={
              booking?.selectedExperiences.find(
                (experienceCount) => experienceCount.id === experience.id,
              )?.count ?? 0
            }
            hasImages={hasImages}
          />
        </Spacer>
      ))}
    </Spacer>
  );
};

ExperienceList.propTypes = {
  updateBooking: PropTypes.func.isRequired,
  experiences: experiencesPropTypes.isRequired,
  booking: bookingPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  experiences: state.experiences,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(ExperienceList);
